import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AtlasFraudDetectionService } from '@secca/core/services/atlas/atlas-fraud-detection.service';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { DialogHelperUtilService } from '@secca/core/services/dialog-helper-util.service';
import { UserService } from '@secca/core/services/user.service';
import { formatAtlasDateTime } from '@secca/shared/helpers/atlas/atlas-date-format-helper';
import { formatAtlasRequestErrorMessage } from '@secca/shared/helpers/atlas/atlas-errorhandler-helper';
import {
  FraudDocumentAnalysis,
  FraudDetectionRequest,
  FraudDetectionResponse,
  FraudIcd10Code,
  FraudMissingInput,
  FraudReview,
  FraudReviewOutcome,
  FraudReviewRequest,
  FraudScoreDetail,
  FraudServiceOrder
} from '@secca/shared/models/atlas/atlas-fraud-detection.model';
import { DropdownDictionary } from '@secca/shared/models/dropdownDictionary';
import {
  AtlasFraudDocumentReportDialogComponent,
  AtlasFraudDocumentReportDialogData
} from '@secca/shared/components/atlas-chatbot/fraud-document-report-dialog/atlas-fraud-document-report-dialog.component';
import { UserDto } from '@secca/shared/models/userDto';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';

export interface AtlasFraudDetectionDialogData {
  caseNumber?: string | null;
  request: Partial<FraudDetectionRequest>;
  onFraudDetectionLoaded?: (result: FraudDetectionResponse) => void;
  onReviewCompleted?: () => void;
  fields: {
    caseNumber: string | null;
    icd10: string | null;
    icd10Source: string | null;
    icd10List: FraudIcd10Code[];
    country: string | null;
    departureDate: string | null;
    incidentDate: string | null;
    arrivalHomeDate: string | null;
  };
}

@Component({
  selector: 'app-atlas-fraud-detection-dialog',
  templateUrl: './atlas-fraud-detection-dialog.component.html',
  styleUrls: ['./atlas-fraud-detection-dialog.component.scss'],
  standalone: false
})
export class AtlasFraudDetectionDialogComponent implements OnInit, OnDestroy {
  loading = false;
  error: string | null = null;
  result: FraudDetectionResponse | null = null;
  showCaseInputs = false;
  showScoreDetails = false;
  showDocuments = false;
  calculationCompletedAt: string | null = null;
  activeTab: 'fraudDetection' | 'previousReviews' = 'fraudDetection';
  reviewRequest: FraudReviewRequest | null = null;
  reviewRequestLoading = false;
  reviewRequestError: string | null = null;
  reviewComment = '';
  reviewOutcome: FraudReviewOutcome | null = null;
  reviewSubmitting = false;
  reviewSubmitError: string | null = null;
  showReviewCompletionSuccess = false;
  private reviewCompletionSuccessTimeout: ReturnType<typeof setTimeout> | null = null;
  private reviewUsers = new Map<string, UserDto>();
  readonly reviewOutcomeOptions: DropdownDictionary[] = [
    new DropdownDictionary('NO_FURTHER_ACTION_NEEDED', 'No further action needed'),
    new DropdownDictionary('ESCALATED_TO_MANAGER', 'Escalated to manager')
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AtlasFraudDetectionDialogData,
    private dialogRef: MatDialogRef<AtlasFraudDetectionDialogComponent>,
    private atlasFraudDetectionService: AtlasFraudDetectionService,
    private dialogHelperUtilService: DialogHelperUtilService,
    private userService: UserService,
    private keycloakService: KeycloakService,
    public dialogBoundryService: DialogBoundryService
  ) {}

  ngOnInit(): void {
    void this.loadFraudDetection();
  }

  ngOnDestroy(): void {
    if (this.reviewCompletionSuccessTimeout) {
      clearTimeout(this.reviewCompletionSuccessTimeout);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  toggleCaseInputs(): void {
    this.showCaseInputs = !this.showCaseInputs;
  }

  toggleScoreDetails(): void {
    this.showScoreDetails = !this.showScoreDetails;
  }

  toggleDocuments(): void {
    this.showDocuments = !this.showDocuments;
  }

  get scoreDetails(): FraudScoreDetail[] {
    return this.sortScoreDetailsDescending(this.deduplicateScoreDetails(this.result?.score_details || []));
  }

  get missingInputs(): FraudMissingInput[] {
    return this.result?.missing_inputs || [];
  }

  get documentAnalyses(): FraudDocumentAnalysis[] {
    return this.result?.documents || [];
  }

  get fraudNotes(): string | null {
    return this.result?.notes?.trim() || null;
  }

  get fraudScoreFactorMessages(): string[] {
    const messages = (this.result?.score_details || [])
      .filter(scoreDetail => this.isScoreDetailFlagged(scoreDetail) || this.getFraudScoreColor(scoreDetail) === 'red')
      .map(scoreDetail => this.getScoreDetailMessage(scoreDetail))
      .filter(Boolean) as string[];

    return Array.from(new Set(messages));
  }

  get numberOfFlags(): number {
    const numberOfFlags = Number(this.result?.number_of_flags);
    if (Number.isFinite(numberOfFlags) && numberOfFlags > 0) {
      return numberOfFlags;
    }

    return (this.result?.score_details || []).filter(scoreDetail => !!scoreDetail.flagged).length;
  }

  get hasRedFraudDetection(): boolean {
    return this.getOverallScoreColor() === 'red';
  }

  get canSubmitReview(): boolean {
    return !!this.reviewRequest &&
      this.reviewRequest.status === 'OPEN' &&
      !!this.reviewOutcome &&
      !!this.reviewComment.trim() &&
      !this.reviewSubmitting;
  }

  get previousReviews() {
    return this.reviewRequest?.reviews || [];
  }

  get hasPreviousReviews(): boolean {
    return this.previousReviews.length > 0;
  }

  get requestServiceOrders(): FraudServiceOrder[] {
    return Array.isArray(this.data?.request?.service_orders) ? this.data.request.service_orders : [];
  }

  get icd10List(): FraudIcd10Code[] {
    if (Array.isArray(this.data?.fields?.icd10List) && this.data.fields.icd10List.length) {
      return this.data.fields.icd10List;
    }

    return this.data?.fields?.icd10 ? [{ code: this.data.fields.icd10, source: this.data.fields.icd10Source || '-' }] : [];
  }

  formatMoney(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Number(value));
  }

  get calculationTimestampText(): string {
    return this.formatDateTime(this.result?.created_at || this.calculationCompletedAt);
  }

  trackByScoreDetail(index: number, scoreDetail: FraudScoreDetail): string {
    return `${scoreDetail?.rule || 'score-detail'}-${scoreDetail?.input || index}`;
  }

  trackByMissingInput(index: number, missingInput: FraudMissingInput): string {
    return `${missingInput?.field || 'missing-input'}-${index}`;
  }

  trackByIcd10(index: number, icd10: FraudIcd10Code): string {
    return `${icd10?.code || 'icd10'}-${icd10?.source || index}`;
  }

  trackByServiceOrder(index: number, serviceOrder: FraudServiceOrder): string {
    return `${serviceOrder?.id || 'service-order'}-${index}`;
  }

  trackByDocumentAnalysis(index: number, documentAnalysis: FraudDocumentAnalysis): string {
    return `${documentAnalysis?.file_name || 'document'}-${index}`;
  }

  trackByReview(index: number, review: { id?: number }): string {
    return `${review?.id || 'review'}-${index}`;
  }

  getReviewUserInitials(review: FraudReview): string {
    const workerIdentifier = (review?.handled_by_username || review?.handled_by || '').trim();
    if (!workerIdentifier) {
      return '-';
    }

    const nameParts = workerIdentifier
      .split(/[\s._-]+/)
      .filter(Boolean);

    if (nameParts.length > 1) {
      return nameParts
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase();
    }

    return workerIdentifier.slice(-3).toUpperCase();
  }

  getReviewUserName(review: FraudReview): string {
    if (this.isLoggedInReviewWorker(review)) {
      return this.getLoggedInUserFullName();
    }

    const user = this.getReviewUser(review);
    return user?.fullName || (review?.handled_by_username || review?.handled_by || '').trim() || 'Unknown worker';
  }

  getReviewUserImage(review: FraudReview): string {
    return this.getReviewUser(review)?.picture || '';
  }

  isReviewUserActive(review: FraudReview): boolean {
    return this.getReviewUser(review)?.active !== false;
  }

  private getLoggedInUserFullName(): string {
    const parsedToken = this.getParsedKeycloakToken();

    return (
      parsedToken?.name ||
      parsedToken?.preferred_username ||
      parsedToken?.upn ||
      parsedToken?.email ||
      'Unknown worker'
    ).trim();
  }

  private isLoggedInReviewWorker(review: FraudReview): boolean {
    const parsedToken = this.getParsedKeycloakToken();
    const currentUserIdentifiers = [
      parsedToken?.sub,
      parsedToken?.preferred_username,
      parsedToken?.upn,
      parsedToken?.email
    ]
      .filter(this.isNonEmptyString)
      .map(identifier => identifier.toLowerCase());
    const reviewUserIdentifiers = [
      review?.handled_by,
      review?.handled_by_username
    ]
      .filter(this.isNonEmptyString)
      .map(identifier => identifier.toLowerCase());

    return reviewUserIdentifiers.some(identifier => currentUserIdentifiers.includes(identifier));
  }

  private getParsedKeycloakToken(): {
    sub?: string;
    name?: string;
    preferred_username?: string;
    upn?: string;
    email?: string;
  } | undefined {
    return this.keycloakService.getKeycloakInstance()?.tokenParsed as {
      sub?: string;
      name?: string;
      preferred_username?: string;
      upn?: string;
      email?: string;
    } | undefined;
  }

  private isNonEmptyString(value: string | null | undefined): value is string {
    return !!value;
  }

  private getReviewUser(review: FraudReview): UserDto | undefined {
    return this.getReviewUserIdentifiers(review)
      .map(identifier => this.reviewUsers.get(identifier.toLowerCase()))
      .find(Boolean);
  }

  getFraudScoreColor(scoreDetail: FraudScoreDetail): 'green' | 'yellow' | 'orange' | 'red';
  getFraudScoreColor(review: FraudReview): 'green' | 'yellow' | 'orange' | 'red';
  getFraudScoreColor(value: FraudScoreDetail | FraudReview | null | undefined): 'green' | 'yellow' | 'orange' | 'red' {
    if (typeof value === 'object' && value !== null) {
      const scoreColor = this.getValidScoreColor(
        (value as FraudScoreDetail).color || (value as FraudReview).fraud_detection_color
      );
      if (scoreColor) {
        return scoreColor;
      }
    }

    return 'green';
  }

  getOverallScoreColor(): 'green' | 'yellow' | 'orange' | 'red' {
    const apiColor = this.getValidScoreColor(this.result?.color);
    if (apiColor) {
      return apiColor;
    }

    return 'green';
  }

  getScoreDetailTone(scoreDetail: FraudScoreDetail): 'match' | 'clear' {
    return scoreDetail?.included_in_total ? 'match' : 'clear';
  }

  private isScoreDetailFlagged(scoreDetail: FraudScoreDetail): boolean {
    return scoreDetail?.flagged === true || String(scoreDetail?.flagged).toLowerCase() === 'true';
  }

  private getScoreDetailMessage(scoreDetail: FraudScoreDetail): string | null {
    return scoreDetail?.message?.trim() || scoreDetail?.note?.trim() || null;
  }

  formatScoreRule(rule: string | null | undefined): string {
    if (!rule) {
      return 'Score detail';
    }

    return rule
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  getDocumentSummary(documentAnalysis: FraudDocumentAnalysis): string {
    return documentAnalysis?.summary || documentAnalysis?.origin_hint || documentAnalysis?.errors?.[0] || 'No summary returned.';
  }

  getDocumentTags(documentAnalysis: FraudDocumentAnalysis): string {
    return (documentAnalysis?.tags || []).filter(Boolean).join(', ') || '-';
  }

  getDocumentFormat(documentAnalysis: FraudDocumentAnalysis): string {
    return documentAnalysis?.format || '-';
  }

  formatReviewOutcome(outcome: FraudReviewOutcome | string | null | undefined): string {
    return this.reviewOutcomeOptions.find(option => option.id === outcome)?.name || '-';
  }

  getReviewIndicatorMessages(review: FraudReview): string[] {
    return (review?.fraud_detection_message || '')
      .split(/\r?\n|;(?=\s*\S)/)
      .map(message => message.trim())
      .filter(Boolean);
  }

  private getValidScoreColor(color: string | null | undefined): 'green' | 'yellow' | 'orange' | 'red' | null {
    const normalizedColor = String(color || '').toLowerCase();
    if (normalizedColor === 'green' || normalizedColor === 'yellow' || normalizedColor === 'orange' || normalizedColor === 'red') {
      return normalizedColor;
    }

    return null;
  }

  openDocumentReport(documentAnalysis: FraudDocumentAnalysis, event?: Event): void {
    event?.stopPropagation();

    this.dialogHelperUtilService.open(AtlasFraudDocumentReportDialogComponent, {
      id: `AtlasFraudDocumentReportDialog-${documentAnalysis.file_name || 'document'}`,
      width: '760px',
      hasBackdrop: false,
      data: {
        documentAnalysis
      } as AtlasFraudDocumentReportDialogData
    });
  }

  async submitReview(): Promise<void> {
    const outcome = this.reviewOutcome;
    if (!this.canSubmitReview || !this.reviewRequest || !outcome) {
      return;
    }

    this.reviewSubmitting = true;
    this.reviewSubmitError = null;

    try {
      await firstValueFrom(
        this.atlasFraudDetectionService.createFraudReview(this.reviewRequest.id, {
          outcome,
          comment: this.reviewComment.trim()
        })
      );
      await this.loadReviewRequest();
      if (this.result) {
        this.result = {
          ...this.result,
          reviewed: true
        };
      }
      this.data?.onReviewCompleted?.();
      this.reviewComment = '';
      this.reviewOutcome = null;
      this.showTemporaryReviewCompletionSuccess();
    } catch (error) {
      this.reviewSubmitError = this.getReviewRequestErrorMessage(error, 'Unable to complete fraud review.');
    } finally {
      this.reviewSubmitting = false;
    }
  }

  private async loadFraudDetection(): Promise<void> {
    if (!this.hasRequestInput()) {
      this.error = 'No fraud detection input is available for this case.';
      this.result = null;
      return;
    }

    this.loading = true;
    this.error = null;
    this.calculationCompletedAt = null;

    try {
      this.result = await firstValueFrom(
        this.atlasFraudDetectionService.detectFraud(this.data.request as FraudDetectionRequest)
      );
      this.data?.onFraudDetectionLoaded?.(this.result);
      this.calculationCompletedAt = new Date().toISOString();
      await this.loadReviewRequest();
    } catch (error) {
      this.error = this.getRequestErrorMessage(error);
      this.result = null;
    } finally {
      this.loading = false;
    }
  }

  private deduplicateScoreDetails(scoreDetails: FraudScoreDetail[]): FraudScoreDetail[] {
    const seen = new Set<string>();
    return scoreDetails.filter(scoreDetail => {
      const key = [
        scoreDetail?.rule || '',
        scoreDetail?.input || '',
        scoreDetail?.matched_value || '',
        scoreDetail?.score == null ? '' : String(scoreDetail.score)
      ].join('|').toLowerCase();

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  private async loadReviewRequest(): Promise<void> {
    const caseNumber = this.result?.case_number || this.data?.caseNumber || this.data?.request?.case_number;
    if (!caseNumber) {
      this.reviewRequest = null;
      this.reviewRequestError = 'No case number is available for the review request.';
      return;
    }

    this.reviewRequestLoading = true;
    this.reviewRequestError = null;
    this.reviewSubmitError = null;

    try {
      this.reviewRequest = await firstValueFrom(
        this.atlasFraudDetectionService.getFraudReviewRequestForCase(String(caseNumber))
      );
      await this.loadReviewUsers();
    } catch (error) {
      this.reviewRequest = null;
      this.reviewRequestError = this.getReviewRequestErrorMessage(error, 'Unable to load review request.');
    } finally {
      this.reviewRequestLoading = false;
    }
  }

  private showTemporaryReviewCompletionSuccess(): void {
    this.showReviewCompletionSuccess = true;
    if (this.reviewCompletionSuccessTimeout) {
      clearTimeout(this.reviewCompletionSuccessTimeout);
    }

    this.reviewCompletionSuccessTimeout = setTimeout(() => {
      this.showReviewCompletionSuccess = false;
      this.reviewCompletionSuccessTimeout = null;
    }, 3000);
  }

  private sortScoreDetailsDescending(scoreDetails: FraudScoreDetail[]): FraudScoreDetail[] {
    return scoreDetails
      .slice()
      .sort((first, second) => Number(second?.score || 0) - Number(first?.score || 0));
  }

  private async loadReviewUsers(): Promise<void> {
    const userIdentifiers = Array.from(new Set(
      (this.reviewRequest?.reviews || [])
        .flatMap(review => this.getReviewUserIdentifiers(review))
        .map(identifier => identifier.toLowerCase())
        .filter(identifier => !this.reviewUsers.has(identifier))
    ));

    await Promise.all(userIdentifiers.map(async identifier => {
      try {
        const user = await firstValueFrom(this.userService.getUserByUserId(identifier));
        this.reviewUsers.set(identifier, user);
        if (user?.userId) {
          this.reviewUsers.set(user.userId.toLowerCase(), user);
        }
      } catch {
        // Keep the review visible even if the user image lookup is unavailable.
      }
    }));
  }

  private getReviewUserIdentifiers(review: FraudReview): string[] {
    return [
      review?.handled_by_username,
      review?.handled_by
    ]
      .filter(this.isNonEmptyString)
      .map(identifier => identifier.trim())
      .filter(Boolean);
  }

  private hasRequestInput(): boolean {
    const request = this.data?.request;
    if (!request) {
      return false;
    }

    const hasIcd10 = Array.isArray(request.icd10) ? request.icd10.length > 0 : !!request.icd10;
    const hasServiceOrders = Array.isArray(request.service_orders) && request.service_orders.length > 0;
    return !!request.case_number || hasIcd10 || !!request.country || hasServiceOrders || !!request.departure_date || !!request.incident_date || !!request.arrival_home_date;
  }

  formatDateTime(value: string | null | undefined): string {
    return formatAtlasDateTime(value);
  }

  private getRequestErrorMessage(error: unknown): string {
    return formatAtlasRequestErrorMessage(error, {
      fallbackMessage: 'Unable to load fraud detection.',
      statusMessage: 'Fraud detection failed'
    });
  }

  private getReviewRequestErrorMessage(error: unknown, fallbackMessage: string): string {
    return formatAtlasRequestErrorMessage(error, {
      fallbackMessage,
      includeStatusWithFallback: true,
      statusMessages: {
        404: 'No review request is linked to this fraud detection yet.'
      }
    });
  }
}
